import { SaveOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { type ButtonProps } from 'antd/lib/button';
import type React from 'react';
import { useMemo } from 'react';
import { useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useFormAugmentedContext } from '../augmented/FormAugmentedContext.tsx';
import { useFormStateIsDirty } from '../useFormStateIsDirty.ts';

export interface ISubmitButtonProps extends ButtonProps {
  /**
   * Messaggio di default da mostrare quando il form è pristine, ovvero
   * se non ci sono modifiche da salvare. Se `null`, il tooltip non viene
   * mostrato.
   */
  pristineMessage?: string | null;
  hideSaveIcon?: boolean;
  /**
   * Se posizionato fuori dal form, il submit non può essere eseuito
   * con `htmlType="submit"`, ma deve essere eseguito manualmente
   */
  isOutsideForm?: boolean;
  /**
   * Abilita il pulsante anche quando il form non è dirty.
   */
  enabledWhenPristine?: boolean;
  /**
   * Tooltip contestuale mostrato quando il pulsante è abilitato o disabilitato
   * dall'esterno, per chiarire quale oggetto si sta salvando e l'impatto del
   * salvataggio. Quando il pulsante è disabilitato perché il form è pristine
   * prevale il messaggio "non ci sono modifiche".
   */
  tooltip?: string;
}

export function SubmitButton(props: ISubmitButtonProps) {
  const {
    pristineMessage,
    hideSaveIcon,
    isOutsideForm,
    children,
    enabledWhenPristine,
    tooltip,
    ...otherProps
  } = props;
  const { t } = useTranslation();

  const { isSubmitting } = useFormState();

  const isDirty = useFormStateIsDirty();
  const { submitForm } = useFormAugmentedContext();

  const { htmlType, onClick } = useMemo(() => {
    if (!isOutsideForm) {
      return {
        htmlType: 'submit' as const,
        onClick: undefined,
      };
    }

    return {
      htmlType: 'button' as const,
      onClick: (e: React.BaseSyntheticEvent) => {
        submitForm.current?.(e).catch((err: any) => {
          console.error(err);
        });
      },
    };
  }, [isOutsideForm, submitForm]);

  // Disabilitato perché il form non ha modifiche (non per prop `disabled` esterna)
  const isPristineDisabled = !(isDirty || enabledWhenPristine);
  const isDisabled = props.disabled ?? isPristineDisabled;

  const tooltipTitle = useMemo(() => {
    // Il messaggio "non ci sono modifiche" va mostrato solo quando il pulsante è
    // disabilitato per form pristine, NON per una disabilitazione esterna (es.
    // permessi): in quel caso il messaggio sarebbe fuorviante se il form è dirty.
    if (props.disabled == null && isPristineDisabled) {
      // Se il messaggio è `null`, non mostriamo il tooltip come descritto nelle props
      if (pristineMessage === null) return undefined;

      return (
        pristineMessage ??
        t(
          'form.submit-button.tooltip-not-dirty',
          'Non ci sono modifiche da salvare',
        )
      );
    }

    // Altrimenti (abilitato, o disabilitato esternamente) mostriamo il tooltip contestuale
    return tooltip;
  }, [props.disabled, isPristineDisabled, pristineMessage, tooltip, t]);

  return (
    <Tooltip title={tooltipTitle}>
      <Button
        icon={hideSaveIcon ? undefined : <SaveOutlined aria-hidden />}
        type="primary"
        htmlType={htmlType}
        onClick={onClick}
        size="large"
        {...otherProps}
        loading={isSubmitting}
        disabled={isDisabled}
      >
        {children ?? t('form.submit-button.save', 'Salva')}
      </Button>
    </Tooltip>
  );
}
